import { useCallback } from "react";

import { useFirebaseNotifications } from "@ui/hooks/useFirebaseNotifications/useFirebaseNotifications";
import { useTranslations } from "@ui/providers/useTranslations";

import { toast } from "@jti/core-components/components/Toaster";
import { useMessaging, useUpdateConsents } from "@jti/rest-api";

type UseWebPushFirebaseNotificationsProps = {
  webPushConsentId?: string;
  onExplicitDecline?: () => void;
  onSubscriptionSuccess?: () => void;
};

export const useWebPushFirebaseNotifications = ({
  webPushConsentId,
  onExplicitDecline,
  onSubscriptionSuccess,
}: UseWebPushFirebaseNotificationsProps) => {
  const { __ } = useTranslations();
  const { subscribeUser, reportDeliveryEvent } = useMessaging();
  const { updateConsents } = useUpdateConsents();

  const updateWebPushConsent = useCallback(
    async (accepted: boolean) => {
      if (!webPushConsentId) throw new Error("Web push consent ID is missing");
      await updateConsents({ consents: [{ id: webPushConsentId, accepted }] });
    },
    [updateConsents, webPushConsentId]
  );

  const subscribeToNotifications = useCallback(
    async (token: string) => {
      await subscribeUser({ token });
      onSubscriptionSuccess?.();
    },
    [onSubscriptionSuccess, subscribeUser]
  );

  const declineNotifications = useCallback(async () => {
    await updateWebPushConsent(false);
    onExplicitDecline?.();
  }, [onExplicitDecline, updateWebPushConsent]);

  const handleFailedSubscription = useCallback(() => {
    toast.error(__("web_notifications.subscription.error"));
  }, [__]);

  const handleMessageReceived = useCallback(
    (reference: string) => reportDeliveryEvent(reference, "received_foreground", { timestamp: Date.now() }),
    [reportDeliveryEvent]
  );
  const handleMessageClicked = useCallback(
    (reference: string) => reportDeliveryEvent(reference, "clicked", { timestamp: Date.now() }),
    [reportDeliveryEvent]
  );

  const syncBrowserOptOut = useCallback(async () => {
    if (!webPushConsentId) return;
    await updateWebPushConsent(false);
  }, [updateWebPushConsent, webPushConsentId]);

  const { requestPermissionAndGetToken } = useFirebaseNotifications({
    onPermissionGranted: () => updateWebPushConsent(true),
    onSuccess: subscribeToNotifications,
    onFail: handleFailedSubscription,
    onDecline: declineNotifications,
    onMessageReceived: handleMessageReceived,
    onMessageClicked: handleMessageClicked,
  });

  return { handleFailedSubscription, requestPermissionAndGetToken, syncBrowserOptOut };
};
